import {
	assistantChartDataId,
	assistantChartImageId,
	assistantContextTypeSchema,
	assistantMessageRoleSchema,
	suretyAccountId,
} from "@inrev/common";
import * as z from "zod";
import { CHAR_LIMIT } from "../message";
import { timeZoneSchema } from "../time-zone";
import * as OrgChart from "./org-chart";

export namespace HistoryMessage {
	export const schema = z.object({
		role: assistantMessageRoleSchema,
		text: z.string().max(CHAR_LIMIT),
	});
}
export type HistoryMessage = z.infer<typeof HistoryMessage.schema>;

export namespace Request {
	export const schema = z.object({
		contextType: assistantContextTypeSchema,
		contextId: z.string(),
		/**
		 * The account every tool call on this turn is scoped to, resolved by apps/api — a bond or
		 * bond request names its owner only through a join the agent has no database to make.
		 * Required: a turn the API cannot scope is refused rather than failing tool by tool.
		 */
		accountId: suretyAccountId,
		message: z.string().min(1).max(CHAR_LIMIT),
		history: z.array(HistoryMessage.schema),
		timeZone: timeZoneSchema,
	});
}
export type Request = z.infer<typeof Request.schema>;

export namespace TextDeltaEvent {
	export const schema = z.object({
		type: z.literal("text_delta"),
		text: z.string(),
	});
}
export type TextDeltaEvent = z.infer<typeof TextDeltaEvent.schema>;

export namespace ToolCallEvent {
	export const schema = z.object({
		type: z.literal("tool_call"),
		id: z.string(),
		toolName: z.string(),
		input: z.record(z.string(), z.unknown()),
	});
}
export type ToolCallEvent = z.infer<typeof ToolCallEvent.schema>;

export namespace ToolResultEvent {
	export const schema = z.object({
		type: z.literal("tool_result"),
		id: z.string(),
		toolName: z.string(),
		isError: z.boolean(),
		result: z.unknown().nullish(),
		resultRowCount: z.number().int().nullish(),
		// The agent's own outcome for the call ("not_found", "invalid_input", "error"). Nullish
		// for the same reason as the usage telemetry: the agent and this api deploy independently.
		status: z.string().nullish(),
	});
}
export type ToolResultEvent = z.infer<typeof ToolResultEvent.schema>;

export namespace ModelCallTiming {
	export const schema = z.object({
		durationMs: z.number().int(),
		firstTokenMs: z.number().int().nullish(),
		inputTokens: z.number().int(),
		outputTokens: z.number().int(),
		cachedInputTokens: z.number().int(),
		thoughtsTokens: z.number().int(),
		outcome: z.enum(["error", "tools", "text", "empty"]),
	});
}
export type ModelCallTiming = z.infer<typeof ModelCallTiming.schema>;

export namespace UsageEvent {
	export const schema = z.object({
		type: z.literal("usage"),
		inputTokens: z.number().int(),
		outputTokens: z.number().int(),
		model: z.string(),
		guidelinesVersion: z.string(),
		policyEdition: z.string(),
		policyHash: z.string(),
		// Per-turn telemetry. Nullish so an agent revision that predates these
		// still validates - this schema is non-strict, so an older api silently strips
		// them rather than dropping the frame, and either deploy order is safe.
		modelCalls: z.number().int().nullish(),
		toolCalls: z.number().int().nullish(),
		// Which ceiling stopped the turn: "model_calls" | "tool_calls". Null when none did.
		budgetHit: z.string().nullish(),
		// Milliseconds of in-tool execution, summed per tool NAME. Distinct from
		// assistantToolCall.durationMs, which this api measures per call from frame arrival.
		toolDurationsMs: z.record(z.string(), z.number().int()).nullish(),
		// ask_document's sub-call tokens, reported separately and NOT included in the totals
		// above -- see persistedTokens, which folds them in exactly once.
		subCallInputTokens: z.number().int().nullish(),
		subCallOutputTokens: z.number().int().nullish(),
		// Hash of the tool manifest the agent built this turn's proxied tools from. Nullish for
		// the same reason as the fields above: the agent and this api deploy independently.
		manifestHash: z.string().nullish(),
		// Knowledge slice ids the agent fetched with get_knowledge. Policy and guidelines are no
		// longer inlined in the prompt, so this is what the answer actually read; each id rebuilds
		// from the assistant_spec_version archive named by the spec fields above.
		knowledgeSlices: z.array(z.string()).nullish(),
		// Of inputTokens, the part Gemini served from its prompt cache. Included in, not added to,
		// inputTokens.
		cachedInputTokens: z.number().int().nullish(),
		// Gemini thinking tokens, summed across the turn. Not included in outputTokens.
		thoughtsTokens: z.number().int().nullish(),
		// One entry per completed model call, in order. durationMs excludes tool execution.
		modelCallTimings: z.array(ModelCallTiming.schema).nullish(),
	});
}
export type UsageEvent = z.infer<typeof UsageEvent.schema>;

export namespace ErrorEvent {
	export const schema = z.object({
		type: z.literal("error"),
		message: z.string(),
		code: z.string().nullish(),
	});
}
export type ErrorEvent = z.infer<typeof ErrorEvent.schema>;

/** A chart PNG is a few hundred KB; this bounds what the api will buffer and store. */
const MAX_CHART_IMAGE_BYTES = 2 * 1024 * 1024;

/**
 * A PNG `run_chart_code` drew. It travels beside the answer, never to the model: the answer carries
 * only a chart block naming `imageId`, and apps/api stores the bytes for the panel to fetch.
 */
export namespace ChartImageEvent {
	export const schema = z.object({
		type: z.literal("chart_image"),
		imageId: assistantChartImageId,
		pngBase64: z.base64().max(Math.ceil(MAX_CHART_IMAGE_BYTES / 3) * 4),
	});
}
export type ChartImageEvent = z.infer<typeof ChartImageEvent.schema>;

/**
 * Structured data a tool assembled for a panel component, such as `ask_org_chart`'s structure.
 * Like a chart image it travels beside the answer, never to the model: the answer carries a chart
 * block naming `chartId`, and apps/api stores the data for the panel to fetch.
 */
export namespace ChartData {
	/**
	 * What apps/api stores and the panel fetches. One kind today; a second is a discriminated union
	 * on `kind`, which the event below then mirrors.
	 */
	export const schema = z.object({ kind: z.literal("orgchart"), data: OrgChart.schema });
}
export type ChartData = z.infer<typeof ChartData.schema>;

export namespace ChartDataEvent {
	export const schema = ChartData.schema.extend({
		type: z.literal("chart_data"),
		chartId: assistantChartDataId,
	});
}
export type ChartDataEvent = z.infer<typeof ChartDataEvent.schema>;

export namespace EventType {
	export const values = [
		"text_delta",
		"tool_call",
		"tool_result",
		"usage",
		"error",
		"chart_image",
		"chart_data",
	] as const;
	export const schema = z.enum(values);
}
export type EventType = z.infer<typeof EventType.schema>;

export const isKnownEventType = (value: unknown): value is EventType =>
	EventType.schema.safeParse(value).success;

export namespace Event {
	export const schema = z.discriminatedUnion("type", [
		TextDeltaEvent.schema,
		ToolCallEvent.schema,
		ToolResultEvent.schema,
		UsageEvent.schema,
		ErrorEvent.schema,
		ChartImageEvent.schema,
		ChartDataEvent.schema,
	]);
}
export type Event = z.infer<typeof Event.schema>;

/** Every event that makes up the answer itself: all but the chart events, which apps/api stores. */
export type AnswerEvent = Exclude<Event, ChartImageEvent | ChartDataEvent>;
